import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material'
import {
  useRouteError,
  isRouteErrorResponse,
  useNavigateTo,
} from 'infrastructure/routing'
import * as Sentry from '@sentry/react'

/**
 * Route-level error fallback displayed in the content area when a route component throws.
 * Keeps AppLayout (nav, drawer) visible so the user can navigate away.
 */
export function RouteErrorFallback() {
  const error = useRouteError()
  const navigate = useNavigateTo()
  const [eventId, setEventId] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (error && !isRouteErrorResponse(error)) {
      setEventId(Sentry.captureException(error))
      return
    }
    setEventId(undefined)
  }, [error])

  const { title, message } = getErrorDetails(error)

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Alert severity="error">
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {message}
        </Typography>
        {error instanceof Error && error.stack && (
          <Box
            component="pre"
            sx={{
              fontSize: '0.75rem',
              overflow: 'auto',
              maxHeight: 200,
              bgcolor: 'grey.100',
              p: 1,
              borderRadius: 1,
              mt: 1,
            }}
          >
            {error.stack}
          </Box>
        )}
        {eventId && (
          <Typography variant="caption" color="text.secondary">
            Error ID: {eventId}
          </Typography>
        )}
      </Alert>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
      </Stack>
    </Box>
  )
}

function getErrorDetails(error: unknown): { title: string; message: string } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return {
        title: 'Page Not Found',
        message: 'The requested page does not exist.',
      }
    }
    return {
      title: `Error ${error.status}`,
      message: error.statusText || 'An unexpected error occurred.',
    }
  }
  if (error instanceof Error) {
    return {
      title: 'Something went wrong',
      message: error.message,
    }
  }
  return {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
  }
}
